/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type SessionRequest } from '../../lib/api/session-requests.js';
import { useTherapists } from '../../lib/queries/clinical.js';
import { useRooms } from '../../lib/queries/rooms.js';
import { useApproveSessionRequest } from '../../lib/queries/session-requests.js';

const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';

/** Admin approves a request → schedules the real session. */
export function ApproveRequestModal({
  request,
  onClose,
}: {
  readonly request: SessionRequest | null;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const approve = useApproveSessionRequest();
  const therapists = useTherapists();
  const rooms = useRooms(true);
  const [start, setStart] = useState('');
  const [therapistId, setTherapistId] = useState('');
  const [roomId, setRoomId] = useState('');

  const onStart = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setStart(e.target.value);
  }, []);
  const onTherapist = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setTherapistId(e.target.value);
  }, []);
  const onRoom = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setRoomId(e.target.value);
  }, []);
  const onModal = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );
  const confirm = useCallback(() => {
    if (request === null || start === '') {
      return;
    }
    approve.mutate(
      {
        id: request.id,
        payload: {
          scheduledStart: new Date(start).toISOString(),
          ...(therapistId !== '' ? { therapistId } : {}),
          ...(roomId !== '' ? { roomId } : {}),
          ...(request.therapyType !== null ? { sessionType: request.therapyType } : {}),
        },
      },
      {
        onSuccess: () => {
          setStart('');
          setTherapistId('');
          setRoomId('');
          onClose();
        },
      },
    );
  }, [approve, request, start, therapistId, roomId, onClose]);

  return (
    <Modal
      open={request !== null}
      onOpenChange={onModal}
      title={t('sessionRequests.approveTitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="ar-start" className={LABEL}>
            {t('sessionRequests.scheduledStart')}
          </label>
          <input
            id="ar-start"
            type="datetime-local"
            className={INPUT}
            value={start}
            onChange={onStart}
          />
        </div>
        <div>
          <label htmlFor="ar-therapist" className={LABEL}>
            {t('sessionRequests.therapist')}
          </label>
          <select id="ar-therapist" className={INPUT} value={therapistId} onChange={onTherapist}>
            <option value="">{t('sessionRequests.anyTherapist')}</option>
            {(therapists.data ?? []).map((th) => (
              <option key={th.id} value={th.id}>
                {th.fullName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ar-room" className={LABEL}>
            {t('sessionRequests.room')}
          </label>
          <select id="ar-room" className={INPUT} value={roomId} onChange={onRoom}>
            <option value="">{t('sessionRequests.noRoom')}</option>
            {(rooms.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end">
          <Button type="button" onClick={confirm} disabled={approve.isPending || start === ''}>
            {t('sessionRequests.approveConfirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
